import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Star, Trash2 } from 'lucide-react';
import type { WebsiteIdea, WebsiteName, IdeaStatus } from '@/types/website';
import { WEBSITE_LABELS, IDEA_STATUS_LABELS } from '@/types/website';

interface IdeaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  idea?: WebsiteIdea | null;
  onSave: (idea: Partial<WebsiteIdea>) => void;
  onDelete?: (id: string) => void;
  defaultWebsite?: WebsiteName;
}

export function IdeaModal({ open, onOpenChange, idea, onSave, onDelete, defaultWebsite }: IdeaModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState<WebsiteName>(defaultWebsite || 'greenvision');
  const [status, setStatus] = useState<IdeaStatus>('nieuw');
  const [impactScore, setImpactScore] = useState<number>(5);
  const [nextStep, setNextStep] = useState('');

  useEffect(() => {
    if (idea) {
      setTitle(idea.title);
      setDescription(idea.description || '');
      setWebsite(idea.website);
      setStatus(idea.status);
      setImpactScore(idea.impact_score || 5);
      setNextStep(idea.next_step || '');
    } else {
      setTitle('');
      setDescription('');
      setWebsite(defaultWebsite || 'greenvision');
      setStatus('nieuw');
      setImpactScore(5);
      setNextStep('');
    }
  }, [idea, defaultWebsite, open]);

  const handleSave = () => {
    if (!title.trim()) return;

    onSave({
      id: idea?.id,
      title: title.trim(),
      description: description.trim() || null,
      website,
      status,
      impact_score: impactScore,
      next_step: nextStep.trim() || null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{idea ? 'Idee bewerken' : 'Nieuw idee'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Idee titel"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Beschrijf het idee..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Website</Label>
              <Select value={website} onValueChange={(v) => setWebsite(v as WebsiteName)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(WEBSITE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as IdeaStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(IDEA_STATUS_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Impact Score
              <span className="flex items-center gap-1 text-amber-600 font-medium">
                <Star className="h-4 w-4 fill-current" />
                {impactScore}
              </span>
            </Label>
            <Slider
              value={[impactScore]}
              onValueChange={([v]) => setImpactScore(v)}
              min={1}
              max={10}
              step={1}
              className="py-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Laag</span>
              <span>Hoog</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="nextStep">Volgende stap</Label>
            <Input
              id="nextStep"
              value={nextStep}
              onChange={(e) => setNextStep(e.target.value)}
              placeholder="Wat is de volgende actie?"
            />
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          {idea && onDelete && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                onDelete(idea.id);
                onOpenChange(false);
              }}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Verwijderen
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSave} disabled={!title.trim()}>
              {idea ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
